// src/pages/CatalogPage.js
//
// Katalog v2: yeni şemadaki (bundle + plan) paketlerin yönetimi.
// Eski ekranlar (E-Sim Paketleri / Ülkeler / Bölgeler) aynen çalışmaya devam eder;
// bu sayfa onların yerine değil YANINA gelir.
//
// İş akışı — sıra önemlidir:
//   1) Katalogu senkronize et (önce dry run)
//   2) Her plana taban fiyat gir
//   3) Bundle'ı yayına al   ← fiyat girilmeden sunucu reddeder (409 BUNDLE_NOT_PRICED)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import styles from './CatalogPage.module.css';
import LoadingSpinner from '../components/common/LoadingSpinner';
import StatusToggle from '../components/common/StatusToggle';
import CatalogSyncModal from '../components/catalog/CatalogSyncModal';
import BundleEditModal from '../components/catalog/BundleEditModal';
import {
    FAMILIES,
    SCOPES,
    familyLabel,
    scopeLabel,
    formFactorLabel,
    fetchAdminBundles,
    publishBundleToPlay,
    updateBundle,
    updateBundlePlan,
    readApiError,
    formatDataAmount,
    formatVoiceSms,
    formatMoney,
    marginPercent,
    hasPricedPlan,
    sortPlans,
} from '../services/catalogApi';

const EMPTY_FILTERS = { family: '', scope: '', country: '', region: '' };

// Fiyat alanı "199.90" gibi string gelir; input için string tutulur.
const asInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const planToEditState = (plan) => ({
    basePriceTry: asInputValue(plan.basePriceTry),
    sortOrder: asInputValue(plan.sortOrder),
    isOpenGp: !!plan.isOpenGp,
    isOpenAp: !!plan.isOpenAp,
    isOpenWeb: !!plan.isOpenWeb,
});

const isValidPrice = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) && n > 0;
};

const CatalogPage = () => {
    const [bundles, setBundles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Sunucuya giden filtreler ile formdaki taslak ayrı tutulur: ülke/bölge
    // yazarken her harfte istek atmasın.
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
    const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
    const [searchTerm, setSearchTerm] = useState(''); // yerel arama (başlık/ülke)
    // Varsayılan: yalnızca aktifler. Anahtar KALDIRILMAMALI — fiyatlanmış ama henüz
    // açılmamış paketler yalnızca pasifler gösterilince bulunup aktif edilebiliyor.
    const [showInactive, setShowInactive] = useState(false);

    const [planEdits, setPlanEdits] = useState({});     // { planId: {…} }
    const [planSaving, setPlanSaving] = useState({});   // { planId: bool }
    const [bundleSaving, setBundleSaving] = useState({}); // { bundleId: bool }
    const [expandedCountries, setExpandedCountries] = useState({}); // { bundleId: bool }

    const [isSyncOpen, setIsSyncOpen] = useState(false);
    const [editingBundle, setEditingBundle] = useState(null);

    // --- Veri çekme ---
    const loadBundles = useCallback(async (filters, { silent = false } = {}) => {
        if (!silent) setIsLoading(true);
        setError(null);
        try {
            const data = await fetchAdminBundles({ ...filters, includeInactive: showInactive });
            setBundles(data);

            // Düzenleme state'i sunucudan gelen değerlerle tazelenir. Fiyat kaydından
            // sonra Google referans fiyatları ve priceId yenilendiği için bu şart.
            const edits = {};
            data.forEach((bundle) => {
                (bundle.plans || []).forEach((plan) => {
                    edits[String(plan.id)] = planToEditState(plan);
                });
            });
            setPlanEdits(edits);
        } catch (err) {
            console.error('Katalog yüklenemedi:', err);
            // Liste ucunda 404 "kayıt yok" demek değil: eşleşme olmadığında sunucu
            // 200 + boş dizi döner. 404 = /v2 uçları bu sunucuda henüz yayında değil.
            if (err?.response?.status === 404) {
                setError('Katalog v2 uçları bu sunucuda henüz yayında değil (404). Backend sürümünü kontrol edin.');
            } else {
                setError(readApiError(err, 'Katalog yüklenirken bir hata oluştu.').message);
            }
            setBundles([]);
        } finally {
            if (!silent) setIsLoading(false);
        }
    }, [showInactive]);

    useEffect(() => {
        loadBundles(appliedFilters);
    }, [appliedFilters, loadBundles]);

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setAppliedFilters({
            family: draftFilters.family,
            scope: draftFilters.scope,
            country: draftFilters.country.trim(),
            region: draftFilters.region.trim(),
        });
    };

    const handleResetFilters = () => {
        setDraftFilters(EMPTY_FILTERS);
        setAppliedFilters(EMPTY_FILTERS);
        setSearchTerm('');
    };

    // --- Yerel arama ---
    const visibleBundles = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return bundles;
        return bundles.filter((bundle) => {
            if ((bundle.title || '').toLowerCase().includes(term)) return true;
            if ((bundle.description || '').toLowerCase().includes(term)) return true;
            return (bundle.countries || []).some((c) =>
                (c.nameSelected || c.name || '').toLowerCase().includes(term)
            );
        });
    }, [bundles, searchTerm]);

    // --- Özet ---
    const summary = useMemo(() => {
        const allPlans = bundles.flatMap((b) => b.plans || []);
        return {
            bundleCount: bundles.length,
            activeCount: bundles.filter((b) => b.isActive).length,
            planCount: allPlans.length,
            unpricedPlanCount: allPlans.filter((p) => !isValidPrice(p.basePriceTry)).length,
        };
    }, [bundles]);

    // --- Plan düzenleme ---
    const setPlanField = (planId, field, value) => {
        setPlanEdits((prev) => ({
            ...prev,
            [planId]: { ...(prev[planId] || {}), [field]: value },
        }));
    };

    // Düzenleme state'i eksik/parçalı olsa bile tüm alanlar dolu bir obje döner.
    const editStateFor = (plan) => ({
        ...planToEditState(plan),
        ...(planEdits[String(plan.id)] || {}),
    });

    // Yalnızca DEĞİŞEN alanları gönder: gönderilmeyen alana dokunulmaz.
    // Fazladan alan göndermek, sadece sıra değiştirirken bile Google Play'e
    // gereksiz bir fiyat push'u tetiklerdi.
    const buildPlanPatch = (plan, edit) => {
        const patch = {};
        if (!edit) return patch;

        const original = planToEditState(plan);

        if (edit.basePriceTry.trim() !== original.basePriceTry) {
            patch.basePriceTry = edit.basePriceTry.trim();
        }
        if (edit.sortOrder.trim() !== original.sortOrder) {
            const n = parseInt(edit.sortOrder, 10);
            patch.sortOrder = Number.isFinite(n) ? n : 0;
        }
        ['isOpenGp', 'isOpenAp', 'isOpenWeb'].forEach((key) => {
            if (edit[key] !== original[key]) patch[key] = edit[key];
        });

        return patch;
    };

    const handleSavePlan = async (plan) => {
        const planId = String(plan.id);
        const patch = buildPlanPatch(plan, editStateFor(plan));

        if (Object.keys(patch).length === 0) {
            toast.info('Değişiklik yok.');
            return;
        }
        if ('basePriceTry' in patch && !isValidPrice(patch.basePriceTry)) {
            toast.error('Taban fiyat pozitif bir sayı olmalı.');
            return;
        }

        setPlanSaving((prev) => ({ ...prev, [planId]: true }));
        try {
            await updateBundlePlan(plan.id, patch);
            toast.success(`Plan ${planId} güncellendi.`);
            // Google push sonrası vergi dahil fiyatlar ve priceId yenilendiği için
            // ekranı sunucudan tazeliyoruz — eski değerleri göstermek "gösterilen ≠
            // tahsil edilen" durumuna yol açardı.
            await loadBundles(appliedFilters, { silent: true });
        } catch (err) {
            console.error(`Plan ${planId} güncellenemedi:`, err);
            const { code, message } = readApiError(err, 'Plan güncellenemedi.');
            toast.error(message);
            // STORE_PUSH_FAILED'de hiçbir alan yazılmadı; kullanıcının girdisi
            // formda kalsın ki tekrar deneyebilsin.
            if (code !== 'STORE_PUSH_FAILED') {
                setPlanEdits((prev) => ({ ...prev, [planId]: planToEditState(plan) }));
            }
        } finally {
            setPlanSaving((prev) => ({ ...prev, [planId]: false }));
        }
    };

    // --- Yayına alma ---
    const [publishing, setPublishing] = useState({});

    const handlePublishToPlay = async (bundle) => {
        const bundleId = String(bundle.id);
        if (!hasPricedPlan(bundle)) {
            toast.error('Önce en az bir plana taban fiyat girin; fiyatsız plan yayına dahil edilmez.');
            return;
        }
        const already = !!bundle.playPublishedAt;
        // Yeniden yayınlamak güvenli (fiyatlar tazelenir) ama Google'a gerçek çağrı;
        // yanlışlıkla tıklamayı onayla.
        const msg = already
            ? `${bundle.title} zaten Play'de yayınlı. Yeniden yayınlamak option fiyatlarını mevcut taban fiyatlardan tazeler. Devam edilsin mi?`
            : `${bundle.title} Google Play'e TEK ürün olarak yayınlanacak (bundle_${bundle.id}). Fiyatı girilmiş her plan bir satın alma seçeneği olur. Devam edilsin mi?`;
        if (!window.confirm(msg)) return;

        setPublishing((prev) => ({ ...prev, [bundleId]: true }));
        try {
            const result = await publishBundleToPlay(bundle.id);
            setBundles((prev) =>
                prev.map((b) => (b.id === bundle.id ? { ...b, playPublishedAt: new Date().toISOString() } : b))
            );
            toast.success(
                `Play'e yayınlandı: ${result.productId} — ${result.pricedPlanCount}/${result.planCount} plan fiyatlandı.`
            );
            if (result.skippedOverPriceCap?.length) {
                // Google, taban TRY'yi ~170 para birimine çevirir ve her ülkenin fiyat
                // tavanı vardır; taşan plan yayından otomatik çıkarılır.
                toast.warn(
                    `${result.skippedOverPriceCap.length} plan bölgesel fiyat tavanına takıldı ve yayına DAHİL EDİLMEDİ: `
                    + `${result.skippedOverPriceCap.join(', ')}. Fiyatı düşürüp yeniden yayınlayın.`,
                    { autoClose: 12000 }
                );
            }
        } catch (err) {
            console.error(`Bundle ${bundleId} Play'e yayınlanamadı:`, err);
            toast.error(readApiError(err, "Play'e yayınlanamadı.").message);
        } finally {
            setPublishing((prev) => ({ ...prev, [bundleId]: false }));
        }
    };

    const handleToggleActive = async (bundle) => {
        const bundleId = String(bundle.id);
        const next = !bundle.isActive;

        if (next && !hasPricedPlan(bundle)) {
            toast.error('Bu paketin hiçbir planında fiyat yok; yayına alınamaz.');
            return;
        }

        setBundleSaving((prev) => ({ ...prev, [bundleId]: true }));
        try {
            await updateBundle(bundle.id, { isActive: next });
            setBundles((prev) =>
                prev.map((b) => (b.id === bundle.id ? { ...b, isActive: next } : b))
            );
            toast.success(next ? 'Paket yayına alındı.' : 'Paket yayından çıkarıldı.');
        } catch (err) {
            console.error(`Bundle ${bundleId} durumu değiştirilemedi:`, err);
            toast.error(readApiError(err, 'Durum değiştirilemedi.').message);
        } finally {
            setBundleSaving((prev) => ({ ...prev, [bundleId]: false }));
        }
    };

    // --- Render yardımcıları ---
    const renderCountries = (bundle) => {
        const countries = bundle.countries || [];
        if (countries.length === 0) return null;
        const expanded = !!expandedCountries[bundle.id];
        const shown = expanded ? countries : countries.slice(0, 6);
        return (
            <div className={styles.countryRow}>
                {shown.map((c) => (
                    <span key={c.id || c.name} className={styles.countryChip}>
                        {c.nameSelected || c.name}
                    </span>
                ))}
                {countries.length > 6 && (
                    <button
                        type="button"
                        className={styles.moreButton}
                        onClick={() =>
                            setExpandedCountries((prev) => ({ ...prev, [bundle.id]: !expanded }))
                        }
                    >
                        {expanded ? 'daha az' : `+${countries.length - 6} ülke`}
                    </button>
                )}
            </div>
        );
    };

    const renderPlanRow = (plan) => {
        const planId = String(plan.id);
        const edit = editStateFor(plan);
        const saving = !!planSaving[planId];

        const patch = buildPlanPatch(plan, edit);
        const isDirty = Object.keys(patch).length > 0;
        // basePriceTry veya isOpenGp gönderildiğinde sunucu Google Play'e push eder.
        const willPushToStore = 'basePriceTry' in patch || 'isOpenGp' in patch;

        // Taban fiyatı olmayan planın Play yayın durumu değiştirilemez (PRICE_REQUIRED).
        const hasBase = isValidPrice(edit.basePriceTry) || isValidPrice(plan.basePriceTry);
        const margin = marginPercent(plan.priceUsd, plan.providerPriceUsd);
        const voiceSms = formatVoiceSms(plan); // ses paketlerinde dolu, diğerlerinde null

        return (
            <tr key={planId} className={isDirty ? styles.dirtyRow : undefined}>
                <td>
                    <div className={styles.planTitle}>
                        {plan.title}
                        {plan.isUnlimited && <span className={styles.unlimitedTag}>sınırsız</span>}
                    </div>
                    <div className={styles.planMeta}>
                        {/* Veri limiti kararı isUnlimited'a göre verilir, dataMb'nin
                            varlığına göre değil. */}
                        <span className="num">{formatDataAmount(plan)}</span>
                        <span>·</span>
                        <span className="num">{plan.validityDays ?? '—'} gün</span>
                        {voiceSms && (
                            <>
                                <span>·</span>
                                <span className={styles.voiceTag}>{voiceSms}</span>
                            </>
                        )}
                        <span>·</span>
                        <span className={`${styles.sku} num`} title="Plan ID = Google Play SKU">
                            SKU {planId}
                        </span>
                    </div>
                </td>

                <td className={styles.numCol}>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={styles.priceInput}
                        value={edit.basePriceTry}
                        onChange={(e) => setPlanField(planId, 'basePriceTry', e.target.value)}
                        disabled={saving}
                        aria-label="Taban fiyat (TL, vergisiz)"
                    />
                    {!isValidPrice(plan.basePriceTry) && (
                        <div className={styles.unpricedHint}>fiyat girilmemiş</div>
                    )}
                </td>

                <td className={styles.numCol}>
                    <div className={`${styles.storePrices} num`}>
                        <span>₺{formatMoney(plan.priceTry)}</span>
                        <span>€{formatMoney(plan.priceEur)}</span>
                        <span>${formatMoney(plan.priceUsd)}</span>
                    </div>
                    {margin !== null && (
                        <div className={styles.marginHint} title="Google USD fiyatı / sağlayıcı maliyeti">
                            marj {margin.toFixed(0)}%
                        </div>
                    )}
                    {!plan.priceId && (
                        <div className={styles.unpricedHint} title="priceId yok — web/PayPal'dan satılamaz">
                            priceId yok
                        </div>
                    )}
                </td>

                <td className={styles.numCol}>
                    <input
                        type="number"
                        step="1"
                        className={styles.orderInput}
                        value={edit.sortOrder}
                        onChange={(e) => setPlanField(planId, 'sortOrder', e.target.value)}
                        disabled={saving}
                        aria-label="Listeleme sırası"
                    />
                </td>

                <td className={styles.toggleCell}>
                    <label className={styles.switch} title={hasBase ? undefined : 'Önce taban fiyat girin'}>
                        <input
                            type="checkbox"
                            checked={edit.isOpenGp}
                            onChange={(e) => setPlanField(planId, 'isOpenGp', e.target.checked)}
                            disabled={saving || !hasBase}
                            aria-label="Google Play'de satışta"
                        />
                        <span className={styles.slider} />
                    </label>
                </td>
                <td className={styles.toggleCell}>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            checked={edit.isOpenAp}
                            onChange={(e) => setPlanField(planId, 'isOpenAp', e.target.checked)}
                            disabled={saving}
                            aria-label="App Store'da satışta"
                        />
                        <span className={styles.slider} />
                    </label>
                </td>
                <td className={styles.toggleCell}>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            checked={edit.isOpenWeb}
                            onChange={(e) => setPlanField(planId, 'isOpenWeb', e.target.checked)}
                            disabled={saving}
                            aria-label="Web'de satışta"
                        />
                        <span className={styles.slider} />
                    </label>
                </td>

                <td className={styles.actionCol}>
                    <button
                        type="button"
                        onClick={() => handleSavePlan(plan)}
                        disabled={saving || !isDirty}
                        className={styles.saveButton}
                    >
                        {saving ? 'Kaydediliyor…' : 'Kaydet'}
                    </button>
                    {isDirty && willPushToStore && (
                        <div className={styles.pushHint}>Google Play'e push edilir</div>
                    )}
                </td>
            </tr>
        );
    };

    const renderBundle = (bundle) => {
        const bundleId = String(bundle.id);
        const priced = hasPricedPlan(bundle);
        const plans = sortPlans(bundle.plans || []);

        return (
            <section key={bundleId} className={styles.bundleCard}>
                <header className={styles.bundleHeader}>
                    {bundle.imgUrl && (
                        <img src={bundle.imgUrl} alt="" className={styles.bundleImage} />
                    )}
                    <div className={styles.bundleInfo}>
                        <div className={styles.bundleTitleRow}>
                            <h2 className={styles.bundleTitle}>{bundle.title}</h2>
                            <span className={styles.badge}>{familyLabel(bundle.family)}</span>
                            <span className={styles.badgeMuted}>{scopeLabel(bundle.scope)}</span>
                            <span className={styles.badgeMuted}>{formFactorLabel(bundle.formFactor)}</span>
                        </div>
                        {bundle.description && (
                            <p className={styles.bundleDescription}>{bundle.description}</p>
                        )}
                        <div className={styles.bundleMeta}>
                            <span className="num">id:{bundleId}</span>
                            {bundle.providerRef && <span className="num">sağlayıcı:#{bundle.providerRef}</span>}
                            {bundle.ipLocation && <span>çıkış: {bundle.ipLocation}</span>}
                            {(bundle.regions || []).length > 0 && (
                                <span>{bundle.regions.join(', ')}</span>
                            )}
                            <span className="num">{plans.length} plan</span>
                        </div>
                        {renderCountries(bundle)}
                    </div>

                    <div className={styles.bundleActions}>
                        <StatusToggle
                            label={`${bundle.title} yayın durumu`}
                            checked={bundle.isActive}
                            saving={!!bundleSaving[bundleId]}
                            disabled={!priced && !bundle.isActive}
                            onChange={() => handleToggleActive(bundle)}
                            onText="Yayında"
                            offText="Yayına kapalı"
                        />
                        <button
                            type="button"
                            className={`${styles.editButton} ghost`}
                            disabled={!!publishing[bundleId] || (!priced && !bundle.playPublishedAt)}
                            title={bundle.playPublishedAt
                                ? `Play'de yayınlı (bundle_${bundleId}) — yeniden yayınlamak fiyatları tazeler`
                                : 'Google Play\'e tek ürün olarak yayınlar; planlar satın alma seçeneği olur'}
                            onClick={() => handlePublishToPlay(bundle)}
                        >
                            {publishing[bundleId]
                                ? 'Yayınlanıyor…'
                                : bundle.playPublishedAt ? "Play ✓ (tazele)" : "Play'e yayınla"}
                        </button>
                        <button
                            type="button"
                            className={`${styles.editButton} ghost`}
                            onClick={() => setEditingBundle(bundle)}
                        >
                            Düzenle
                        </button>
                    </div>
                </header>

                {!priced && (
                    <div className={styles.lockedNote}>
                        Bu paketin hiçbir planında taban fiyat yok. <strong>Yayına alınamaz</strong> —
                        önce en az bir plana fiyat girin.
                    </div>
                )}

                <div className={styles.tableWrap}>
                    <table className={styles.planTable}>
                        <thead>
                            <tr>
                                <th>Plan</th>
                                <th className={styles.numCol}>Taban ₺<span className={styles.thHint}>vergisiz</span></th>
                                <th className={styles.numCol}>Google<span className={styles.thHint}>vergi dahil, salt okunur</span></th>
                                <th className={styles.numCol}>Sıra</th>
                                <th className={styles.toggleCol}>Play</th>
                                <th className={styles.toggleCol}>App Store</th>
                                <th className={styles.toggleCol}>Web</th>
                                <th className={styles.actionCol}>İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {plans.length > 0 ? (
                                plans.map(renderPlanRow)
                            ) : (
                                <tr>
                                    <td colSpan={9} className={styles.emptyCell}>
                                        Bu paketin planı yok.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        );
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.title}>Katalog (v2)</h1>
                    <p className={styles.subtitle}>
                        Yeni şemadaki paketler ve planları. Sırası: senkronize et → fiyat gir → yayına al.
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <button
                        type="button"
                        className="ghost"
                        onClick={() => loadBundles(appliedFilters)}
                        disabled={isLoading}
                    >
                        Yenile
                    </button>
                    <button type="button" onClick={() => setIsSyncOpen(true)}>
                        Katalogu senkronize et
                    </button>
                </div>
            </div>

            <div className={styles.summaryRow}>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Paket</span>
                    <span className={`${styles.summaryValue} num`}>{summary.bundleCount}</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Yayında</span>
                    <span className={`${styles.summaryValue} num`}>{summary.activeCount}</span>
                </div>
                <div className={styles.summaryItem}>
                    <span className={styles.summaryLabel}>Plan</span>
                    <span className={`${styles.summaryValue} num`}>{summary.planCount}</span>
                </div>
                <div className={`${styles.summaryItem} ${summary.unpricedPlanCount > 0 ? styles.summaryWarn : ''}`}>
                    <span className={styles.summaryLabel}>Fiyatsız plan</span>
                    <span className={`${styles.summaryValue} num`}>{summary.unpricedPlanCount}</span>
                </div>
            </div>

            <form className={styles.filters} onSubmit={handleFilterSubmit}>
                <div className={styles.filterGroup}>
                    <label htmlFor="cat-family">Ürün ailesi</label>
                    <select
                        id="cat-family"
                        value={draftFilters.family}
                        onChange={(e) => setDraftFilters((p) => ({ ...p, family: e.target.value }))}
                    >
                        <option value="">Tümü</option>
                        {FAMILIES.map((f) => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="cat-scope">Kapsam</label>
                    <select
                        id="cat-scope"
                        value={draftFilters.scope}
                        onChange={(e) => setDraftFilters((p) => ({ ...p, scope: e.target.value }))}
                    >
                        <option value="">Tümü</option>
                        {SCOPES.map((s) => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </select>
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="cat-country">Ülke (İngilizce)</label>
                    <input
                        id="cat-country"
                        type="text"
                        placeholder="Portugal"
                        value={draftFilters.country}
                        onChange={(e) => setDraftFilters((p) => ({ ...p, country: e.target.value }))}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="cat-region">Bölge</label>
                    <input
                        id="cat-region"
                        type="text"
                        placeholder="Europe"
                        value={draftFilters.region}
                        onChange={(e) => setDraftFilters((p) => ({ ...p, region: e.target.value }))}
                    />
                </div>
                <div className={styles.filterGroup}>
                    <label htmlFor="cat-search">Listede ara</label>
                    <input
                        id="cat-search"
                        type="search"
                        placeholder="Başlık veya ülke"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className={styles.filterToggle}>
                    <StatusToggle
                        label="Pasif paketleri göster"
                        checked={showInactive}
                        disabled={isLoading}
                        onChange={(e) => setShowInactive(e.target.checked)}
                        onText="Pasifler gösteriliyor"
                        offText="Pasifleri göster"
                    />
                </div>
                <div className={styles.filterActions}>
                    <button type="submit" disabled={isLoading}>Filtrele</button>
                    <button type="button" className="ghost" onClick={handleResetFilters} disabled={isLoading}>
                        Sıfırla
                    </button>
                </div>
            </form>

            <div className={styles.contentArea}>
                {isLoading ? (
                    <div className={styles.loadingContainer}><LoadingSpinner /></div>
                ) : error ? (
                    <div className={styles.errorPanel}>
                        <span className={styles.errorIcon} aria-hidden="true">⚠</span>
                        <p className={styles.errorMessage}>{error}</p>
                        <button type="button" className="ghost" onClick={() => loadBundles(appliedFilters)}>
                            Tekrar dene
                        </button>
                    </div>
                ) : visibleBundles.length === 0 ? (
                    <div className={styles.emptyState}>
                        <p>Filtrelerinize uygun {showInactive ? '' : 'aktif '}paket bulunamadı.</p>
                        {!showInactive && (
                            <p className={styles.emptyHint}>
                                Pasif paketler gizli. Henüz yayına alınmamış bir paketi arıyorsanız “Pasifleri göster”i açın.
                            </p>
                        )}
                        <p className={styles.emptyHint}>
                            Yeni şemada henüz paket yoksa önce “Katalogu senkronize et” ile sağlayıcıdan çekin.
                        </p>
                    </div>
                ) : (
                    visibleBundles.map(renderBundle)
                )}
            </div>

            <CatalogSyncModal
                isOpen={isSyncOpen}
                onClose={() => setIsSyncOpen(false)}
                onApplied={() => loadBundles(appliedFilters, { silent: true })}
            />

            <BundleEditModal
                isOpen={!!editingBundle}
                bundle={editingBundle}
                onClose={() => setEditingBundle(null)}
                onSaved={() => loadBundles(appliedFilters, { silent: true })}
            />
        </div>
    );
};

export default CatalogPage;
